import { makeAutoObservable, runInAction } from 'mobx';
import { mergeById } from '../pages/RoomInspector/groupMessages';
import {
  emptyInspectorQuery,
  type InspectorQuery,
} from '../pages/RoomInspector/inspectorQuery';
import { chronologicalMessages } from '../pages/RoomInspector/messageMetadata';
import {
  type ArtifactPreview,
  type InspectionArtifact,
  type InspectionMessage,
  type InspectionRail,
  type InspectionSnapshot,
  type InspectionTurn,
  type RoomOverview,
  roomInspectorApi,
  type UserOverview,
  type UserRoom,
} from '../services/roomInspectorApi';

function errorText(error: unknown): string {
  const value = error as {
    response?: { status: number; data?: { message?: string } };
    message?: string;
  };
  if (value.response?.status === 401) return '登录已失效，请重新登录';
  if (value.response?.status === 403) return '当前账号没有房间巡检权限';
  if (value.response?.status === 404) return '房间、会话或产物不存在，或已删除';
  return value.response?.data?.message || value.message || '加载失败，请重试';
}

// Each mounted page owns its own store; changing identities clears sensitive
// data immediately, and late responses cannot repopulate a previous view.
export class RoomInspectorStore {
  query: InspectorQuery = { ...emptyInspectorQuery };
  overview: RoomOverview | null = null;
  userOverview: UserOverview | null = null;
  rooms: UserRoom[] = [];
  roomsCursor = '';
  relation = 'all';
  includeLeft = false;
  rail: InspectionRail | null = null;
  snapshot: InspectionSnapshot | null = null;
  artifacts: InspectionArtifact[] = [];
  artifactsCursor = '';
  artifactScope: 'session' | 'room' = 'session';
  preview: ArtifactPreview | null = null;
  previewFile: Blob | null = null;
  previewOpen = false;
  loading = false;
  sessionLoading = false;
  historyLoading = false;
  artifactsLoading = false;
  previewLoading = false;
  roomsLoading = false;
  projectsLoading = false;
  loadingSections = new Set<string>();
  error = '';
  sessionError = '';
  artifactError = '';
  previewError = '';
  private epoch = 0;
  private sessionEpoch = 0;
  private artifactEpoch = 0;
  private previewEpoch = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
  private handleError(
    error: unknown,
    field:
      | 'error'
      | 'sessionError'
      | 'artifactError'
      | 'previewError' = 'error',
  ) {
    const status = (error as { response?: { status?: number } }).response
      ?.status;
    if (status === 401 || status === 403) {
      this.dispose();
      this.error = errorText(error);
      return;
    }
    this[field] = errorText(error);
  }
  dispose() {
    this.epoch++;
    this.sessionEpoch++;
    this.artifactEpoch++;
    this.previewEpoch++;
    this.query = { ...emptyInspectorQuery };
    this.overview = null;
    this.userOverview = null;
    this.rooms = [];
    this.roomsCursor = '';
    this.rail = null;
    this.snapshot = null;
    this.artifacts = [];
    this.artifactsCursor = '';
    this.preview = null;
    this.previewFile = null;
    this.previewOpen = false;
    this.loading = false;
    this.sessionLoading = false;
    this.historyLoading = false;
    this.artifactsLoading = false;
    this.previewLoading = false;
    this.roomsLoading = false;
    this.projectsLoading = false;
    this.loadingSections.clear();
    this.error = '';
    this.sessionError = '';
    this.artifactError = '';
    this.previewError = '';
  }
  get selectedUserAbsent() {
    return Boolean(
      this.query.userId &&
        this.overview &&
        !this.overview.members.some(
          (member) => member.user.userId === this.query.userId,
        ),
    );
  }

  async loadQuery(query: InspectorQuery) {
    const sameRoom =
      this.query.roomId === query.roomId && this.query.userId === query.userId;
    this.query = query;
    if (sameRoom && this.overview && this.rail) {
      await this.selectSession(query.sessionId);
      return;
    }
    const epoch = ++this.epoch;
    this.sessionEpoch++;
    this.artifactEpoch++;
    this.previewEpoch++;
    this.overview = null;
    this.userOverview = null;
    this.rail = null;
    this.snapshot = null;
    this.rooms = [];
    this.roomsCursor = '';
    this.artifacts = [];
    this.artifactsCursor = '';
    this.preview = null;
    this.previewFile = null;
    this.previewOpen = false;
    this.loadingSections.clear();
    this.error = '';
    this.sessionError = '';
    this.artifactError = '';
    this.previewError = '';
    this.loading = Boolean(query.roomId || query.userId);
    this.sessionLoading = false;
    this.historyLoading = false;
    this.artifactsLoading = false;
    this.previewLoading = false;
    this.roomsLoading = false;
    this.projectsLoading = false;
    try {
      if (query.roomId) {
        const [overview, rail] = await Promise.all([
          roomInspectorApi.room(query.roomId),
          roomInspectorApi.rail(query.roomId, query.userId),
        ]);
        if (epoch !== this.epoch) return;
        runInAction(() => {
          this.overview = overview;
          this.rail = rail;
          this.loading = false;
        });
        if (query.userId) void this.loadRooms();
        await this.selectSession(query.sessionId);
      } else if (query.userId) {
        const [overview, page] = await Promise.all([
          roomInspectorApi.user(query.userId),
          roomInspectorApi.userRooms(query.userId, {
            relation: this.relation,
            includeLeft: this.includeLeft,
            pageSize: 20,
          }),
        ]);
        if (epoch !== this.epoch) return;
        runInAction(() => {
          this.userOverview = overview;
          this.rooms = page.rooms;
          this.roomsCursor = page.nextCursor;
        });
      }
    } catch (error) {
      if (epoch === this.epoch)
        runInAction(() => {
          this.handleError(error);
        });
    } finally {
      if (epoch === this.epoch)
        runInAction(() => {
          this.loading = false;
        });
    }
  }
  async loadRooms(more = false) {
    if (!this.query.userId || this.roomsLoading) return;
    const epoch = this.epoch;
    this.roomsLoading = true;
    try {
      const page = await roomInspectorApi.userRooms(this.query.userId, {
        relation: this.relation,
        includeLeft: this.includeLeft,
        cursor: more ? this.roomsCursor : '',
        pageSize: 20,
      });
      if (epoch === this.epoch)
        runInAction(() => {
          this.rooms = more
            ? mergeById(this.rooms, page.rooms, (item) => item.room.roomId)
            : page.rooms;
          this.roomsCursor = page.nextCursor;
        });
    } catch (error) {
      if (epoch === this.epoch)
        runInAction(() => {
          this.handleError(error);
        });
    } finally {
      if (epoch === this.epoch)
        runInAction(() => {
          this.roomsLoading = false;
        });
    }
  }
  async filterRooms(relation: string, includeLeft: boolean) {
    if (this.roomsLoading) return;
    this.relation = relation;
    this.includeLeft = includeLeft;
    this.error = '';
    this.rooms = [];
    this.roomsCursor = '';
    await this.loadRooms();
  }
  async loadSection(key: string, pinned = false) {
    const section = pinned
      ? this.rail?.pinned
      : this.rail?.sections.find((item) => item.railSectionKey === key);
    const loadingKey = pinned ? 'pinned' : key;
    if (!section?.nextCursor || this.loadingSections.has(loadingKey)) return;
    const epoch = this.epoch;
    this.loadingSections.add(loadingKey);
    try {
      const page = await roomInspectorApi.sessions(this.query.roomId, {
        creatorUserId: this.query.userId,
        sectionKey: pinned ? '' : key,
        pinned,
        cursor: section.nextCursor,
        pageSize: 20,
      });
      if (epoch === this.epoch)
        runInAction(() => {
          section.sessions = mergeById(
            section.sessions,
            page.sessions,
            (item) => item.agentSessionId,
          );
          section.nextCursor = page.nextCursor;
        });
    } catch (error) {
      if (epoch === this.epoch)
        runInAction(() => {
          this.handleError(error);
        });
    } finally {
      if (epoch === this.epoch)
        runInAction(() => {
          this.loadingSections.delete(loadingKey);
        });
    }
  }
  async moreProjects() {
    const rail = this.rail;
    if (!rail?.nextProjectsCursor || this.projectsLoading) return;
    const epoch = this.epoch;
    this.projectsLoading = true;
    try {
      const page = await roomInspectorApi.rail(
        this.query.roomId,
        this.query.userId,
        rail.nextProjectsCursor,
      );
      if (epoch === this.epoch)
        runInAction(() => {
          rail.projects = mergeById(
            rail.projects,
            page.projects,
            (item) => item.projectId,
          );
          const keys = new Set(
            rail.sections.map((item) => item.railSectionKey),
          );
          rail.sections.push(
            ...page.sections.filter((item) => !keys.has(item.railSectionKey)),
          );
          rail.nextProjectsCursor = page.nextProjectsCursor;
        });
    } catch (error) {
      if (epoch === this.epoch)
        runInAction(() => {
          this.handleError(error);
        });
    } finally {
      if (epoch === this.epoch)
        runInAction(() => {
          this.projectsLoading = false;
        });
    }
  }
  async selectSession(id: string) {
    const epoch = ++this.sessionEpoch;
    this.snapshot = null;
    this.sessionError = '';
    this.sessionLoading = Boolean(id);
    this.historyLoading = false;
    this.closePreview();
    void this.loadArtifacts();
    if (!id) return;
    try {
      const roomId = this.query.roomId;
      const result = await roomInspectorApi.snapshot(roomId, id);
      if (epoch !== this.sessionEpoch) return;
      // A complete snapshot already contains the first message. Long sessions
      // use the server's bounded forward cursor instead of downloading all history.
      if (result.snapshot.historyNextCursor) {
        const { history } = await roomInspectorApi.messages(
          roomId,
          id,
          'start',
        );
        if (epoch !== this.sessionEpoch) return;
        result.snapshot.messages = history.items;
        result.snapshot.turns = history.turns;
        result.snapshot.historyNextCursor = history.nextCursor;
      }
      if (epoch === this.sessionEpoch)
        runInAction(() => {
          this.snapshot = {
            ...result.snapshot,
            messages: chronologicalMessages(result.snapshot.messages),
          };
        });
    } catch (error) {
      if (epoch === this.sessionEpoch)
        runInAction(() => {
          this.handleError(error, 'sessionError');
        });
    } finally {
      if (epoch === this.sessionEpoch)
        runInAction(() => {
          this.sessionLoading = false;
        });
    }
  }
  async moreMessages(pages = 1) {
    const snapshot = this.snapshot;
    if (!snapshot?.historyNextCursor || this.historyLoading) return;
    const epoch = this.sessionEpoch;
    const roomId = this.query.roomId;
    const sessionId = snapshot.session.agentSessionId;
    this.historyLoading = true;
    this.sessionError = '';
    try {
      let cursor = snapshot.historyNextCursor;
      let messages: InspectionMessage[] = [];
      let turns: InspectionTurn[] = [];
      const cursors = new Set<string>();
      for (let page = 0; page < Math.min(pages, 3) && cursor; page++) {
        cursors.add(cursor);
        const { history } = await roomInspectorApi.messages(
          roomId,
          sessionId,
          cursor,
        );
        if (epoch !== this.sessionEpoch) return;
        if (history.nextCursor && cursors.has(history.nextCursor))
          throw new Error('后续消息暂时无法继续加载，请重试');
        messages = mergeById(history.items, messages, (item) => item.messageId);
        turns = mergeById(history.turns, turns, (item) => item.turnId);
        cursor = history.nextCursor;
      }
      if (epoch === this.sessionEpoch)
        runInAction(() => {
          snapshot.messages = chronologicalMessages(
            mergeById(messages, snapshot.messages, (item) => item.messageId),
          );
          snapshot.turns = mergeById(
            turns,
            snapshot.turns,
            (item) => item.turnId,
          );
          snapshot.historyNextCursor = cursor;
        });
    } catch (error) {
      if (epoch === this.sessionEpoch)
        runInAction(() => {
          this.handleError(error, 'sessionError');
        });
    } finally {
      if (epoch === this.sessionEpoch)
        runInAction(() => {
          this.historyLoading = false;
        });
    }
  }
  async setArtifactScope(scope: 'session' | 'room') {
    this.artifactScope = scope;
    await this.loadArtifacts();
  }
  async loadArtifacts(more = false) {
    if (!this.query.roomId || (more && this.artifactsLoading)) return;
    const epoch = ++this.artifactEpoch;
    this.artifactError = '';
    this.artifactsLoading = true;
    if (!more) {
      this.artifacts = [];
      this.artifactsCursor = '';
    }
    if (this.artifactScope === 'session' && !this.query.sessionId) {
      this.artifactsLoading = false;
      return;
    }
    try {
      const page = await roomInspectorApi.artifacts(this.query.roomId, {
        sessionId:
          this.artifactScope === 'session' ? this.query.sessionId : undefined,
        cursor: more ? this.artifactsCursor : '',
        pageSize: 20,
      });
      if (epoch === this.artifactEpoch)
        runInAction(() => {
          this.artifacts = more
            ? mergeById(
                this.artifacts,
                page.artifacts,
                (item) => item.artifactId,
              )
            : page.artifacts;
          this.artifactsCursor = page.nextCursor;
        });
    } catch (error) {
      if (epoch === this.artifactEpoch)
        runInAction(() => {
          this.handleError(error, 'artifactError');
        });
    } finally {
      if (epoch === this.artifactEpoch)
        runInAction(() => {
          this.artifactsLoading = false;
        });
    }
  }
  async openPreview(id: string) {
    const epoch = ++this.previewEpoch;
    this.preview = null;
    this.previewFile = null;
    this.previewError = '';
    this.previewOpen = true;
    this.previewLoading = true;
    try {
      const preview = await roomInspectorApi.preview(this.query.roomId, id);
      if (epoch !== this.previewEpoch) return;
      if (epoch === this.previewEpoch)
        runInAction(() => {
          this.preview = preview;
        });
      if (preview.artifactType === 'file') {
        const file = await roomInspectorApi.fileContent(this.query.roomId, id);
        if (epoch === this.previewEpoch)
          runInAction(() => {
            this.previewFile = file;
          });
      }
    } catch (error) {
      if (epoch === this.previewEpoch)
        runInAction(() => {
          this.handleError(error, 'previewError');
        });
    } finally {
      if (epoch === this.previewEpoch)
        runInAction(() => {
          this.previewLoading = false;
        });
    }
  }
  closePreview() {
    this.previewEpoch++;
    this.preview = null;
    this.previewFile = null;
    this.previewOpen = false;
    this.previewLoading = false;
  }
}
